import React, { useState, useRef, useEffect } from "react";
import { jarvisChat } from "@/lib/api";

/**
 * UniversalCommandBar — always-visible bottom-of-screen text input.
 *
 * Lets the owner type ANY instruction in their own words and have Jarvis
 * try to fulfil it (works without a microphone). The same `/api/jarvis/chat`
 * endpoint as voice — the brain decides the action. If it doesn't recognise
 * the request it returns a `clarify` or `universal` action and the user can
 * pick from suggestions.
 *
 * Slash-commands for quick wins:
 *   /note <text>        → save a note
 *   /weather <city>     → save home weather city
 *   /trade buy gold 100 → place a £100 buy on gold
 *   /brief              → fire the morning brief
 *   /story | /teach     → tell-me-a-story / teach-me-something
 */
export default function UniversalCommandBar({ onReply, onAction, speak }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [lastQuestion, setLastQuestion] = useState("");
  const ref = useRef(null);

  // Cmd+K / Ctrl+K → focus the bar
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        ref.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    const text = value.trim();
    if (!text || busy) return;
    setBusy(true);
    setHint("");
    // Clear the input straight away so the owner can queue the next command
    // even while the brain is still thinking — no more 30s "stuck disabled" feel.
    setValue("");
    try {
      onReply?.({ role: "owner", text });
      const r = await jarvisChat(text);
      onReply?.({ role: "jarvis", text: r.reply, action: r.action, suggested_replies: r.suggested_replies });
      if (r.action) onAction?.(r.action, r.reply);
      const note = r.action?.result?.spoken_note;
      const spoken = note ? `${r.reply || ""} ${note}`.trim() : r.reply;
      speak?.(spoken);
      if (r.action?.type === "universal") {
        setHint(`Couldn't match that — try "show me the cheat sheet" or rephrase, sir.`);
      }
    } catch (err) {
      setHint(`Error: ${err.message || err}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="universal-cmd-bar"
      data-testid="universal-command-bar"
      role="search"
    >
      <div className="universal-cmd-inner">
        <span className="universal-cmd-prefix">→</span>
        <input
          ref={ref}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Type anything…"
          disabled={false}
          autoComplete="off"
          spellCheck="false"
          data-testid="universal-command-input"
          aria-label="Universal command input"
        />
        <kbd className="universal-cmd-hint">⌘K</kbd>
        <button
          type="submit"
          className="universal-cmd-go"
          disabled={busy || !value.trim()}
          data-testid="universal-command-submit"
        >
          {busy ? "…" : "GO"}
        </button>
      </div>
      {hint && <div className="universal-cmd-hint-line" data-testid="universal-command-hint">{hint}</div>}
    </form>
  );
}
